import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Popover,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import BlockIcon from '@mui/icons-material/Block';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

const statusColor = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'CURRENT':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    case 'SKIPPED':
      return 'default';
    case 'REJECTED':
      return 'error';
    default:
      return 'info';
  }
};

const StepIcon = ({ status }) => {
  const s = (status || '').toUpperCase();
  if (s === 'COMPLETED') return <CheckCircleIcon color="success" fontSize="small" />;
  if (s === 'CURRENT') return <RadioButtonCheckedIcon color="warning" fontSize="small" />;
  if (s === 'SKIPPED' || s === 'REJECTED') return <BlockIcon color="disabled" fontSize="small" />;
  return <CircleOutlinedIcon color="disabled" fontSize="small" />;
};

const formatLogTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const LogList = ({ logs, maxHeight = 280 }) => (
  <Stack spacing={0.75} sx={{ overflowY: 'auto', maxHeight }}>
    {logs.map((log) => (
      <Box
        key={log.approvalWorkflowLogId || `${log.actionTaken}-${log.actionAt}`}
        sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #eee' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {log.actionTaken}
          {log.stepName ? ` · ${log.stepName}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {log.actionByName || 'System'} · {formatLogTime(log.actionAt)}
          {log.comments ? ` · ${log.comments}` : ''}
        </Typography>
      </Box>
    ))}
  </Stack>
);

/**
 * Workflow stepper + sneak-peek log with option to pin inline.
 */
const ApprovalWorkflowPanel = ({ progress }) => {
  const [logAnchor, setLogAnchor] = useState(null);
  const [showLogInSection, setShowLogInSection] = useState(false);
  const steps = useMemo(() => progress?.steps || [], [progress]);
  const activeStepIndex = useMemo(() => {
    const idx = steps.findIndex((s) => (s.stepStatus || '').toUpperCase() === 'CURRENT');
    if (idx >= 0) return idx;
    if ((progress?.workflowStatus || '').toUpperCase() === 'COMPLETED') return steps.length;
    return Math.max(0, steps.findIndex((s) => (s.stepStatus || '').toUpperCase() !== 'COMPLETED'));
  }, [steps, progress]);

  const logs = useMemo(() => {
    const list = Array.isArray(progress?.logs) ? [...progress.logs] : [];
    return list.sort((a, b) => new Date(b.actionAt || 0) - new Date(a.actionAt || 0));
  }, [progress]);

  if (!progress || !progress.approvalWorkflowProgressId) {
    return null;
  }

  const openPeek = (e) => setLogAnchor(e.currentTarget);
  const closePeek = () => setLogAnchor(null);
  const pinToSection = () => {
    setShowLogInSection(true);
    closePeek();
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Workflow
        </Typography>
        {logs.length > 0 && (
          <Tooltip title={showLogInSection ? 'Workflow log is shown below' : 'Sneak peek workflow log'}>
            <IconButton
              size="small"
              onClick={openPeek}
              aria-label="Workflow log"
              color={showLogInSection ? 'primary' : 'default'}
            >
              <Badge badgeContent={logs.length} color="primary" max={99}>
                <InfoOutlinedIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {progress.workflowName || 'Workflow'} · Status: {progress.workflowStatus}
        {progress.currentStepName ? ` · Current: ${progress.currentStepName}` : ''}
        {progress.assignedRoleName ? ` · Role: ${progress.assignedRoleName}` : ''}
      </Typography>

      {progress.canCurrentUserAct === false && progress.authorizationMessage && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
          {progress.authorizationMessage}
        </Alert>
      )}

      {steps.length > 0 && (
        <Box sx={{ mb: 2, overflowX: 'auto', px: 0.5 }}>
          <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ minWidth: steps.length * 120 }}>
            {steps.map((step) => (
              <Step
                key={step.approvalWorkflowStepId || step.stepSequence}
                completed={(step.stepStatus || '').toUpperCase() === 'COMPLETED'}
              >
                <StepLabel
                  StepIconComponent={() => <StepIcon status={step.stepStatus} />}
                  optional={(
                    <Typography variant="caption" color="text.secondary">
                      {step.assignedRoleName || step.stepType || 'Step'}
                    </Typography>
                  )}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {step.stepName}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <Stack spacing={1} divider={<Divider flexItem />}>
        {steps.map((step) => (
          <Box key={step.approvalWorkflowStepId || step.stepSequence}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {step.stepSequence}. {step.stepName}
                {step.stepType && step.stepType !== 'APPROVAL' ? ` (${step.stepType})` : ''}
              </Typography>
              <Chip size="small" label={step.stepStatus || 'PENDING'} color={statusColor(step.stepStatus)} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Role: {step.assignedRoleName || 'Any'}
              {step.conditionExpression
                ? ` · Condition: ${step.conditionExpression}`
                : ' · Always run'}
              {step.nextStepOnApprove != null ? ` · On Approve → ${step.nextStepOnApprove}` : ''}
              {step.nextStepOnReject != null ? ` · On Reject → ${step.nextStepOnReject}` : ''}
              {step.nextStepOnFalse != null ? ` · On False → ${step.nextStepOnFalse}` : ''}
            </Typography>
          </Box>
        ))}
      </Stack>

      {showLogInSection && logs.length > 0 && (
        <Box sx={{ mt: 2, p: 1.25, bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e6e6e6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Workflow Log
            </Typography>
            <Tooltip title="Hide from section">
              <IconButton size="small" onClick={() => setShowLogInSection(false)}>
                <UnfoldLessIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <LogList logs={logs} maxHeight={220} />
        </Box>
      )}

      {logs.some((l) => l.actionTaken === 'SKIP') && (
        <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
          One or more steps were skipped because their conditions were not met.
        </Alert>
      )}

      <Popover
        open={Boolean(logAnchor)}
        anchorEl={logAnchor}
        onClose={closePeek}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: '90vw', maxHeight: 420, p: 1.5 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Workflow Log
          </Typography>
          {!showLogInSection && (
            <Button size="small" onClick={pinToSection} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              Show in section
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Sneak peek — pin it to the Workflow section if you want it always visible.
        </Typography>
        <LogList logs={logs} maxHeight={300} />
      </Popover>
    </Box>
  );
};

export default ApprovalWorkflowPanel;
